// File: webelieve-frontend/src/features/admin/components/settings/intake-forms/FieldPalette.tsx
// EF-6.1: Draggable field type palette for the form builder

import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import type { FormFieldType } from './intakeFormApi';

interface FieldTypeConfig {
  type: FormFieldType;
  label: string;
  category: 'basic' | 'contact' | 'choice' | 'advanced';
}

const FIELD_TYPES: FieldTypeConfig[] = [
  // Basic
  { type: 'text', label: 'Text', category: 'basic' },
  { type: 'textarea', label: 'Long Text', category: 'basic' },
  { type: 'number', label: 'Number', category: 'basic' },
  { type: 'date', label: 'Date', category: 'basic' },
  // Contact
  { type: 'email', label: 'Email', category: 'contact' },
  { type: 'phone', label: 'Phone', category: 'contact' },
  { type: 'address', label: 'Address', category: 'contact' },
  // Choice
  { type: 'select', label: 'Dropdown', category: 'choice' },
  { type: 'multiselect', label: 'Multi-Select', category: 'choice' },
  { type: 'checkbox', label: 'Checkbox', category: 'choice' },
  // Advanced
  { type: 'file', label: 'File Upload', category: 'advanced' },
  { type: 'currency', label: 'Currency', category: 'advanced' },
];

const CATEGORY_LABELS: Record<string, string> = {
  basic: 'Basic Fields',
  contact: 'Contact Info',
  choice: 'Choice Fields',
  advanced: 'Advanced',
};

const FIELD_ICONS: Record<FormFieldType, string> = {
  text: 'T',
  textarea: '¶',
  number: '#',
  date: '📅',
  email: '@',
  phone: '☎',
  address: '🏠',
  select: '▼',
  multiselect: '☰',
  checkbox: '☑',
  file: '📎',
  currency: '$',
};

const FieldPalette: React.FC = () => {
  const grouped = FIELD_TYPES.reduce((acc, ft) => {
    if (!acc[ft.category]) acc[ft.category] = [];
    acc[ft.category].push(ft);
    return acc;
  }, {} as Record<string, FieldTypeConfig[]>);

  return (
    <div className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="p-3">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Field Types
        </h3>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-3">
          Drag fields onto the canvas
        </p>

        <Droppable droppableId="palette" isDropDisabled={true}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
              {Object.entries(grouped).map(([category, types]) => (
                <div key={category}>
                  <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase mb-1.5">
                    {CATEGORY_LABELS[category]}
                  </p>
                  <div className="space-y-1">
                    {types.map((ft, index) => {
                      const globalIndex = FIELD_TYPES.indexOf(ft);
                      return (
                        <Draggable
                          key={ft.type}
                          draggableId={`palette-${ft.type}`}
                          index={globalIndex}
                        >
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={`flex items-center gap-2 px-2.5 py-1.5 border rounded-md cursor-grab text-sm transition-colors ${
                                snapshot.isDragging
                                  ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft shadow-md'
                                  : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-lex-accent dark:hover:border-lex-accent'
                              }`}
                            >
                              <span className="w-5 text-center text-xs text-gray-400">
                                {FIELD_ICONS[ft.type]}
                              </span>
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                {ft.label}
                              </span>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                  </div>
                </div>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
};

export default FieldPalette;
export { FIELD_TYPES, FIELD_ICONS };
